import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Typography,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Radio,
  Space,
  Button,
  Tabs,
  Splitter,
  Switch,
  Alert,
  Skeleton,
  Drawer,
  Modal,
  message as antdMessage,
} from 'antd';
import { LeftOutlined, EyeInvisibleOutlined, PlusOutlined, TeamOutlined, MessageOutlined, RobotOutlined, CalendarOutlined, CheckCircleOutlined, ExclamationCircleOutlined, BranchesOutlined, LockOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import { withRouter } from 'react-router-dom';
import useCampaignCreateStore from '../../stores/useCampaignCreateStore';
import InterviewMessageInput from '../interview-invites/InterviewMessageInput';
import ModuleGroupPreview from '../module-group/ModuleGroupPreview';
import UserAvatar from '../common/UserAvatar';
import LivePhonePreview from './LivePhonePreview';
import CalendarPreview from '../calendar-viewer/CalendarPreview';
import CampaignAvailabilitySettings from './CampaignAvailabilitySettings';
import CalendarOwnerPicker from '../common/CalendarOwnerPicker';
import { useFeature, FeatureLockCard } from '../common/FeatureGate';
import ImportContactsModal from '../contacts/ImportContactsModal';
import { findInvalidMergeTokens, formatInvalidTokenMessage } from '../../utils/mailmerge';
import session from '../../utils/session';
import { getUserSettings } from '../../reducers/integrations/resources';
import './CreateCampaignV3.css';

const { Title, Text } = Typography;

const CreateCampaignV3 = ({ history, location }) => {
  const [calendarDrawerOpen, setCalendarDrawerOpen] = React.useState(false);
  const [flowModalOpen, setFlowModalOpen] = React.useState(false);

  const {
    loading,
    submitting,
    savingDraft,
    scheduleLapsed,
    userHasCalendar,
    userCalendarSettings,
    userCalendarConnection,
    groups,
    timezones,
    users,
    moduleGroups,
    customContactFields,
    form,
    showImportModal,
    showPreflightModal,
    validationResult,
    calendarPreview,
    previewTerminal,
    previewResetKey,
    previewAudience,
    previewSimulateNoSlots,
    setShowImportModal,
    setShowPreflightModal,
    clearValidationResult,
    refreshGroupsAndSelect,
    initialize,
    setFormField,
    setAudienceMode,
    validateCampaign,
    submitCampaign,
    saveAsDraft,
    fetchCalendarPreview,
    clearCalendarPreview,
    setPreviewTerminal,
    resetPreview,
    loadPreviewAudience,
    cyclePreviewContact,
    setPreviewSimulateNoSlots,
  } = useCampaignCreateStore();

  const previewContact = (previewAudience?.contacts || [])[previewAudience?.index || 0] || null;

  // Scheduling-manager: gate the "book on a teammate's calendar" picker.
  const hasSchedulingManager = useFeature('scheduling-manager');
  // interview-scheduling: gate the whole calendar/booking side of a broadcast —
  // agents that book interviews, the calendar panel and the schedule summary.
  const canSchedule = useFeature('interview-scheduling');

  const selectedModuleGroup = (moduleGroups || []).find(mg => mg.id === form.moduleGroupId);
  const calendarRequired = selectedModuleGroup?.calendar_required || false;
  // Reachable only via an existing draft once the Select disables these agents,
  // but a saved draft can still put us here — so every calendar affordance and
  // preview fetch keys off this, not off calendarRequired alone.
  const calendarUiEnabled = calendarRequired && canSchedule;

  // Scan all message-bearing fields for unknown {{mailmerge}} tokens so we can
  // surface inline errors and block submit before the backend silently strips
  // them at render time. Extra whitelist = the company's custom contact fields
  // (CSV-imported columns, integration-populated keys), so tokens like
  // `{{job_title}}` resolve when contacts have that detail row.
  const extraValidTokens = useMemo(
    () => new Set((customContactFields || []).map((f) => String(f).toLowerCase())),
    [customContactFields],
  );
  const invalidTokens = useMemo(() => ({
    message: findInvalidMergeTokens(form.message, extraValidTokens),
    noSlotsMessage: findInvalidMergeTokens(form.noSlotsMessage, extraValidTokens),
    moduleOverrides: Object.fromEntries(
      Object.entries(form.moduleResponseOverrides || {})
        .map(([id, txt]) => [id, findInvalidMergeTokens(txt, extraValidTokens)])
    ),
  }), [form.message, form.noSlotsMessage, form.moduleResponseOverrides, extraValidTokens]);

  useEffect(() => {
    const draftCampaignId = location?.state?.draftCampaign?.id;
    const templateDraft = location?.state?.templateDraft;
    const preselectedGroupId = location?.state?.groupId;
    initialize(draftCampaignId, templateDraft, preselectedGroupId).catch(() => {
      antdMessage.error('Failed to load campaign create data');
    });
  }, []);

  const getCalendarFromDate = useCallback(() => {
    const tz = form.timezone
      ? (timezones || []).find((t) => t.value === form.timezone)?.zone || moment.tz.guess(true)
      : moment.tz.guess(true);
    const base = form.scheduleDate ? moment(form.scheduleDate) : moment();
    return base.tz(tz).format('YYYY-MM-DDTHH:mm:ssZ');
  }, [form.scheduleDate, form.timezone, timezones]);

  // Close calendar drawer and clear preview when conditions no longer met
  useEffect(() => {
    if (form.messageType !== 'auto' || !calendarUiEnabled || !userHasCalendar) {
      setCalendarDrawerOpen(false);
      clearCalendarPreview();
    }
  }, [form.messageType, calendarUiEnabled, userHasCalendar]);

  // Auto-fetch/refresh calendar preview when relevant form fields change
  useEffect(() => {
    if (form.messageType !== 'auto' || !calendarUiEnabled || !userHasCalendar || !form.moduleGroupId) return;
    fetchCalendarPreview(getCalendarFromDate());
  }, [form.scheduleDate, form.timezone, form.moduleGroupId, form.availabilitySettings, getCalendarFromDate]);

  // Scheduling-manager: when the campaign books on a single teammate, the preflight
  // schedule/calendar rows must reflect THAT teammate, not the sender. Resolve the
  // owner id (single, non-self) and load their calendar settings for the summary.
  const previewOwnerId = (hasSchedulingManager
    && form.messageType === 'auto'
    && calendarUiEnabled
    && form.schedulingMode === 'single'
    && form.schedulingUserId
    && String(form.schedulingUserId) !== String((session.get('userData') || {}).id || ''))
    ? form.schedulingUserId
    : null;
  const previewOwnerName = useMemo(
    () => (previewOwnerId
      ? ((users || []).find((u) => String(u.id) === String(previewOwnerId))?.name || 'Selected teammate')
      : null),
    [previewOwnerId, users],
  );
  const [ownerCalendarSettings, setOwnerCalendarSettings] = useState(null);
  useEffect(() => {
    if (!previewOwnerId) { setOwnerCalendarSettings(null); return undefined; }
    let cancelled = false;
    getUserSettings(previewOwnerId)
      .then((res) => { if (!cancelled) setOwnerCalendarSettings(res?.data || null); })
      .catch(() => { if (!cancelled) setOwnerCalendarSettings(null); });
    return () => { cancelled = true; };
  }, [previewOwnerId]);

  // Round-robin books each candidate on a different teammate, so a single
  // calendar/constraints line can't represent it — show a "varies by teammate"
  // treatment instead of the sender's own calendar.
  const previewRoundRobin = hasSchedulingManager
    && form.messageType === 'auto'
    && calendarUiEnabled
    && form.schedulingMode === 'round_robin'
    && (form.schedulingUserIds || []).length > 0;
  const roundRobinCount = (form.schedulingUserIds || []).length;

  // Per-owner available-slot counts bubbled up from the calendar-owner picker(s),
  // used to block sending an interview campaign that can't be booked (0 slots).
  // Memoized + change-guarded so the picker's onSlotCounts effect can't loop.
  const [pickerSlotCounts, setPickerSlotCounts] = useState({});
  const handleSlotCounts = useCallback((incoming) => {
    setPickerSlotCounts((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(incoming || {}).forEach((k) => {
        if (next[k] !== incoming[k]) { next[k] = incoming[k]; changed = true; }
      });
      return changed ? next : prev;
    });
  }, []);

  const handleSubmit = async () => {
    if (form.messageType === 'auto' && form.moduleGroupId) {
      const selectedMG = (moduleGroups || []).find(mg => mg.id === form.moduleGroupId);
      if (selectedMG?.module_count === 0) {
        antdMessage.error('This agent has no modules — please pick another agent or add modules first');
        return;
      }
      if (selectedMG?.calendar_required && !canSchedule) {
        antdMessage.error('Interview scheduling is not included in your plan');
        return;
      }
      if (selectedMG?.calendar_required && !userHasCalendar) {
        antdMessage.error('Please connect your calendar before using this agent');
        return;
      }
      // Scheduling-manager: if the manager explicitly chose whose calendar to
      // book on, make sure they actually chose. Round-robin starts with an empty
      // set, so an unfilled picker would otherwise pass silently and the backend
      // would fall back to booking every interview on the creator's calendar.
      if (selectedMG?.calendar_required && hasSchedulingManager) {
        if (form.schedulingMode === 'round_robin'
            && (form.schedulingUserIds || []).length < 2) {
          antdMessage.error('Select at least two teammates for round-robin scheduling');
          return;
        }
        if (form.schedulingMode === 'single' && !form.schedulingUserId) {
          antdMessage.error('Select whose calendar interviews should book on');
          return;
        }
      }
    }
    if (form.messageType !== 'auto' && !form.message?.trim()) {
      antdMessage.error('Message is required');
      return;
    }
    if (form.scheduleType === 'scheduled' && (!form.scheduleDate || !form.timezone)) {
      antdMessage.error('Schedule date and timezone are required');
      return;
    }
    if (form.audienceMode === 'group' && !form.groupId) {
      antdMessage.error('Please select a group');
      return;
    }
    if (form.audienceMode === 'import' && !form.groupName?.trim()) {
      antdMessage.error('Please provide a name for the new imported group');
      return;
    }

    // Gate on unknown {{mailmerge}} tokens, scoped to the active editor mode.
    // 'custom' only sends form.message; 'auto' only sends module overrides +
    // no-slots. Bad tokens left in the inactive mode are ignored at submit
    // since they won't be sent.
    const moduleNameById = (id) => {
      const mg = selectedModuleGroup;
      const mod = (mg?.modules || []).find((m) => m.id === id);
      return mod?.title || mod?.name || 'module';
    };
    const allBad = form.messageType === 'auto'
      ? [
        { label: 'No Slots Message', tokens: invalidTokens.noSlotsMessage },
        ...Object.entries(invalidTokens.moduleOverrides).map(([id, toks]) => ({
          label: `Module "${moduleNameById(id)}"`,
          tokens: toks,
        })),
      ].filter((b) => b.tokens.length)
      : [
        { label: 'Custom Message', tokens: invalidTokens.message },
      ].filter((b) => b.tokens.length);

    if (allBad.length) {
      const first = allBad[0];
      const shown = first.tokens.slice(0, 5).join(', ');
      const overflow = first.tokens.length > 5 ? ` (+${first.tokens.length - 5} more)` : '';
      const more = allBad.length > 1
        ? ` (+${allBad.length - 1} more field${allBad.length > 2 ? 's' : ''})`
        : '';
      antdMessage.error(`Unknown merge fields in ${first.label}: ${shown}${overflow}${more}`);
      return;
    }

    const result = await validateCampaign();
    if (!result.ok) {
      antdMessage.error(result.message || 'Validation failed');
      return;
    }
    resetPreview();
    setShowPreflightModal(true);
    // Load the audience for the contact cycler regardless of message type —
    // {{firstname}} etc. resolve against the selected contact for both
    // broadcast and agent campaigns.
    loadPreviewAudience();
  };

  const handleCreate = async () => {
    // Guard: no broadcast bot means the campaign can't actually send. The Send
    // button is disabled for this, but block here too in case it's reached.
    if (validationResult?.meta?.hasCampaignBot === false) {
      antdMessage.error('This company has no broadcast campaign bot — set one up before sending a broadcast');
      return;
    }
    const result = await submitCampaign();
    if (!result.ok) {
      antdMessage.error(result.message || 'Failed to create campaign');
      return;
    }
    setShowPreflightModal(false);
    const excluded = result.excludedArchivedCount || 0;
    const sentTo = result.contactCount;
    let successMsg = result.message || 'Campaign created successfully';
    if (excluded > 0) {
      const sentPart = sentTo != null ? `Campaign sent to ${sentTo} contact${sentTo === 1 ? '' : 's'}` : 'Campaign sent';
      successMsg = `${sentPart} (${excluded} archived contact${excluded === 1 ? '' : 's'} excluded)`;
    }
    antdMessage.success(successMsg);
    history.push('/campaign');
  };

  const handleCancelPreflight = () => {
    setShowPreflightModal(false);
    clearValidationResult();
  };

  // Helpers for preflight display
  const getGroupName = () => {
    if (form.audienceMode !== 'group') return null;
    const group = (groups || []).find((g) => String(g.id) === String(form.groupId));
    return group?.name || 'Unknown group';
  };

  const getTimezoneName = () => {
    const tz = (timezones || []).find((t) => t.value === form.timezone);
    return tz?.name || form.timezone || 'Auto-detected';
  };

  const getNotificationUserObjects = () => {
    if (!form.notificationUsers.length) return [];
    return form.notificationUsers.map((uid) => {
      const u = (users || []).find((user) => String(user.id) === String(uid));
      return u || { id: uid, name: uid };
    });
  };

  const getModuleGroupName = () => {
    const mg = (moduleGroups || []).find((m) => m.id === form.moduleGroupId);
    return mg?.name || 'Unknown agent';
  };

  // Summarise schedule constraints for the verify modal. Branches on whether
  // the campaign has its own availability override or is using the staff
  // user's default calendar settings. Returns { sourceLabel, lines, valid }.
  const buildScheduleSummary = () => {
    const av = form.availabilitySettings;
    const fmtDuration = (h, m) => {
      const parts = [];
      if (h) parts.push(`${h}h`);
      if (m) parts.push(`${m}m`);
      return parts.join(' ') || '0m';
    };

    // ── Branch A: campaign-level override (specific dates) ──
    if (av && av.mode === 'specific_dates') {
      const dates = Array.isArray(av.specificDates) ? av.specificDates : [];
      const validDates = dates.filter((d) => d && d.date && d.startTime && d.endTime);
      const datesPreview = validDates
        .slice(0, 2)
        .map((d) => `${moment(d.date).format('MMM D')}: ${d.startTime} – ${d.endTime}`)
        .join(' · ');
      const datesLine = validDates.length === 0
        ? 'No specific dates set'
        : validDates.length > 2
          ? `${datesPreview} · +${validDates.length - 2} more`
          : datesPreview;

      const duration = fmtDuration(av.hrDuration || 0, av.minDuration || 0);
      const seq = av.scheduleSequence === 'next-available' ? 'next-available' : 'random';
      const maxPer = av.maxPerInterviewSlot || 1;
      const slotLine = `${duration} interviews · ${maxPer} per slot · ${seq}`;

      return {
        sourceLabel: '(campaign override)',
        lines: [datesLine, slotLine],
        valid: validDates.length > 0,
      };
    }

    // ── Branch B: user-level default availability ──
    // Scheduling-manager: prefer the chosen teammate's settings so the preflight
    // shows their availability, not the sender's.
    const s = ownerCalendarSettings || userCalendarSettings || {};
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    // selectedDays is an array of ACTIVE day NUMBERS (0=Sun..6=Sat) — e.g. [1,3,5]
    // for Mon/Wed/Fri — matching how the backend reads it (selectedDays.includes(dayNum)).
    // It is NOT a boolean-per-index array; treating it as one shifted the days (MWF → Sun–Tue).
    const selected = Array.isArray(s.selectedDays) ? s.selectedDays : [];
    const selectedIdx = [...new Set(selected.map(Number))]
      .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
      .sort((a, b) => a - b);

    // Humanise day list: prefer contiguous ranges (Mon–Fri) or weekend/weekday shortcuts.
    const humaniseDays = (idxs) => {
      if (idxs.length === 0) return 'No days selected';
      if (idxs.length === 7) return 'Every day';
      const weekdays = [1, 2, 3, 4, 5];
      const weekends = [0, 6];
      const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
      if (same(idxs, weekdays)) return 'Mon–Fri';
      if (same(idxs, weekends)) return 'Weekends';
      // Collapse contiguous runs into ranges
      const ranges = [];
      let i = 0;
      while (i < idxs.length) {
        let j = i;
        while (j + 1 < idxs.length && idxs[j + 1] === idxs[j] + 1) j += 1;
        ranges.push(i === j ? dayNames[idxs[i]] : `${dayNames[idxs[i]]}–${dayNames[idxs[j]]}`);
        i = j + 1;
      }
      return ranges.join(', ');
    };

    const daysStr = humaniseDays(selectedIdx);
    const hasTimes = !!(s.startTime && s.endTime);
    const tz = s.zone ? ` ${s.zone}` : '';
    const daysLine = hasTimes
      ? `${daysStr}, ${s.startTime} – ${s.endTime}${tz}`
      : daysStr;

    const duration = fmtDuration(s.hrDuration || 0, s.minDuration || 0);
    const seq = s.scheduleSequence === 'next-available' ? 'next-available' : 'random';
    const maxPer = s.maxPerInterviewSlot || 1;
    const buffer = s.noInterviewsWithin && s.noInterviewsWithin !== '0h'
      ? ` · ${s.noInterviewsWithin} buffer`
      : '';
    const slotLine = `${duration} interviews · ${maxPer} per slot · ${seq}${buffer}`;

    return {
      sourceLabel: ownerCalendarSettings && previewOwnerName
        ? `(${previewOwnerName}'s default availability)`
        : '(your default availability)',
      lines: [daysLine, slotLine],
      valid: selectedIdx.length > 0 && hasTimes,
    };
  };

  // Round-robin: each teammate books on their own availability, so summarise it as
  // "varies by teammate" rather than any one calendar's constraints.
  const buildRoundRobinScheduleSummary = () => {
    const strategyLabel = form.schedulingStrategy === 'even_split' ? 'even split' : 'least-loaded';
    return {
      sourceLabel: `(round-robin · ${strategyLabel})`,
      lines: [
        `Varies by teammate — ${roundRobinCount} calendar${roundRobinCount === 1 ? '' : 's'}`,
        'Each interview books on the assigned teammate’s own availability',
      ],
      valid: true,
    };
  };

  const scheduleSummary = (form.messageType === 'auto' && calendarRequired && userHasCalendar)
    ? (previewRoundRobin ? buildRoundRobinScheduleSummary() : buildScheduleSummary())
    : null;

  // Scheduling-manager: summarise whose calendar interviews will book on for the
  // preflight. Only shown when it differs from the default (the creator's own
  // calendar, already covered by the "Calendar" row) — i.e. a chosen teammate or
  // a round-robin set.
  const bookingOwnerSummary = (() => {
    if (form.messageType !== 'auto' || !calendarUiEnabled || !hasSchedulingManager) return null;
    const nameOf = (uid) => {
      const u = (users || []).find((x) => String(x.id) === String(uid));
      return u?.name || 'Selected teammate';
    };
    if (form.schedulingMode === 'round_robin') {
      const ids = form.schedulingUserIds || [];
      if (!ids.length) return null;
      const strategyLabel = form.schedulingStrategy === 'even_split' ? 'Even split' : 'Least-loaded';
      return { title: `Round-robin — ${strategyLabel}`, value: ids.map(nameOf).join(', ') };
    }
    const currentUserId = String((session.get('userData') || {}).id || '');
    const uid = form.schedulingUserId;
    if (!uid || String(uid) === currentUserId) return null; // self default — nothing extra to show
    return { title: 'Booking calendar', value: nameOf(uid) };
  })();

  // A company with no broadcast campaign bot cannot actually send a broadcast, so
  // block creation while still letting the user open the preflight to see why.
  // meta.hasCampaignBot is only known once validation has run.
  const noCampaignBot = validationResult?.meta?.hasCampaignBot === false;

  // Availability guard: block sending an interview campaign whose booking calendar
  // has 0 available slots. Round-robin blocks only when NONE of the selected
  // teammates have any slots. A null/undefined count = still fetching → don't block.
  const bookingHasNoSlots = (() => {
    if (form.messageType !== 'auto' || !calendarUiEnabled) return false;
    if (previewRoundRobin) {
      const counts = (form.schedulingUserIds || []).map((id) => pickerSlotCounts[id]);
      if (!counts.length || counts.some((c) => c === null || c === undefined)) return false; // loading/none selected
      return counts.reduce((sum, c) => sum + (c || 0), 0) === 0;
    }
    if (previewOwnerId) {
      const c = pickerSlotCounts[previewOwnerId];
      if (c === null || c === undefined) return false; // still fetching
      return c === 0;
    }
    // Self (no teammate override): the sender's own calendar preview.
    if (calendarPreview.loading) return false;
    return (calendarPreview.availableSlotCount || 0) === 0;
  })();

  const handleSaveDraft = async () => {
    if (!form.groupId && form.audienceMode === 'group') {
      antdMessage.error('Please select a group to save a draft');
      return;
    }
    if (form.audienceMode === 'import' && !form.groupId) {
      antdMessage.error('Please finalize group creation before saving a draft');
      return;
    }
    if (saveAsDraft) {
      const res = await saveAsDraft();
      if (res?.ok) {
        antdMessage.success('Draft saved successfully');
        history.push('/campaign');
      } else {
        antdMessage.error(res?.message || 'Failed to save draft');
      }
    }
  };

  const getPrivacyOptions = () => [
    { label: <span style={{ fontWeight: 'normal' }}><span style={{color: '#52c41a'}}>●</span> Public</span>, value: 'public' },
    { label: <span style={{ fontWeight: 'normal' }}><EyeInvisibleOutlined /> Private</span>, value: 'private' },
  ];

  const tagRender = (props) => {
    const { value, closable, onClose } = props;
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const u = (users || []).find(user => String(user.id) === String(value));
    if (!u) return null;
    
    const name = u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim();

    return (
      <span
        onMouseDown={onPreventMouseDown}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: '#f0f0f0',
          border: '1px solid #d9d9d9',
          borderRadius: '16px',
          padding: '2px 8px 2px 2px',
          marginRight: '4px',
          marginBottom: '2px',
          marginTop: '2px'
        }}
      >
        <UserAvatar name={name} src={u.avatar_url || u.avatar || u.photo} userId={u.id} size={24} showTooltip={false} />
        <span style={{ fontSize: '13px', color: '#262626' }}>{name}</span>
        {closable && (
          <span 
            onClick={onClose} 
            style={{ cursor: 'pointer', color: '#8c8c8c', marginLeft: '2px', display: 'flex', alignItems: 'center' }}
          >
            ×
          </span>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="interview-invites-wrapper" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#FAFAFA' }}>
        {/* Header skeleton */}
        <div style={{ background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)', borderBottom: '1px solid #d9f7be', padding: '12px 20px' }}>
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col xs={24} md={16}>
              <Space direction="vertical" size={6}>
                <Skeleton.Input active size="large" style={{ width: 220 }} />
                <Skeleton.Input active size="small" style={{ width: 280 }} />
              </Space>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
              <Space size="middle">
                <Skeleton.Button active style={{ width: 90 }} />
                <Skeleton.Button active style={{ width: 130 }} />
              </Space>
            </Col>
          </Row>
        </div>

        {/* Body skeleton — 3 panels */}
        <div style={{ flex: 1, display: 'flex', height: 'calc(100vh - 140px)' }}>
          {/* Left panel */}
          <div style={{ width: 320, padding: '12px 24px', background: '#fff', overflowY: 'auto' }}>
            <Skeleton active title={{ width: 120 }} paragraph={{ rows: 0 }} style={{ marginBottom: 12 }} />
            <Skeleton.Input active block style={{ marginBottom: 16 }} />
            <Skeleton.Input active style={{ width: 180, marginBottom: 16 }} />
            <Skeleton.Input active block style={{ marginBottom: 24 }} />
            <div style={{ height: 1, background: '#f0f0f0', margin: '16px 0' }} />
            <Skeleton active title={{ width: 110 }} paragraph={{ rows: 0 }} style={{ marginBottom: 12 }} />
            <Skeleton.Input active style={{ width: 200, marginBottom: 24 }} />
            <div style={{ height: 1, background: '#f0f0f0', margin: '16px 0' }} />
            <Skeleton active title={{ width: 120 }} paragraph={{ rows: 0 }} style={{ marginBottom: 12 }} />
            <Skeleton.Input active block style={{ marginBottom: 8 }} />
            <Skeleton.Input active size="small" style={{ width: 180 }} />
          </div>

          {/* Middle panel */}
          <div style={{ flex: 1, background: '#fff', borderLeft: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
              <Skeleton.Input active style={{ width: 320 }} />
            </div>
            <div style={{ padding: 24, flex: 1 }}>
              <Skeleton active paragraph={{ rows: 1, width: '90%' }} style={{ marginBottom: 16 }} />
              <Skeleton.Input active block size="large" style={{ height: 140, marginBottom: 16 }} />
              <Skeleton.Input active style={{ width: 260 }} />
            </div>
          </div>

          {/* Right panel — phone preview */}
          <div style={{ width: 330, background: '#fafafa', padding: '12px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Skeleton active title={{ width: 60 }} paragraph={{ rows: 0 }} style={{ alignSelf: 'flex-start', marginBottom: 24 }} />
            <div style={{ width: 260, height: 520, borderRadius: 32, border: '2px solid #e8e8e8', background: '#fff', padding: '40px 16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Skeleton.Input active style={{ width: 120, height: 14, borderRadius: 8 }} />
              <Skeleton.Input active block style={{ height: 48, borderRadius: 12 }} />
              <Skeleton.Input active style={{ width: 160, height: 36, borderRadius: 12 }} />
              <Skeleton.Input active style={{ width: 100, height: 28, borderRadius: 12 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="interview-invites-wrapper" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#FAFAFA' }}>
      <div
        style={{
            background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
            borderBottom: '1px solid #d9f7be',
            padding: '12px 20px'
        }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col xs={24} md={16}>
                <Space direction="vertical" size={6}>
                    <Title level={3} style={{ margin: 0, color: '#1f2937' }}>
                        <Button type="link" icon={<LeftOutlined />} onClick={() => history.push('/campaign')} style={{ padding: 0, marginRight: 8, color: '#1f2937' }} />
                        Create Campaign
                    </Title>
                    <Text style={{ fontSize: 15, color: '#4b5563' }}>
                        Send a text blast to a saved audience.
                    </Text>
                </Space>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                <Space size="middle">
                  <Button onClick={handleSaveDraft} loading={savingDraft} disabled={submitting}>Save draft</Button>
                  <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={savingDraft} style={{ backgroundColor: '#38b549', borderColor: '#38b549' }}>
                    Verify & Proceed
                  </Button>
                </Space>
            </Col>
        </Row>
      </div>

      <div style={{ flex: 1, position: 'relative' }}>
        <Splitter style={{ height: 'calc(100vh - 140px)', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)' }}>

          <Splitter.Panel defaultSize={320} min={280} max="40%">
             <div style={{ padding: '12px 24px', background: '#fff', height: '100%', overflowY: 'auto' }}>
                 <div style={{ marginBottom: 24 }}>
                     <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Campaign Info</Title>
                     <Space direction="vertical" style={{ width: '100%' }} size="small">
                        <div>
                          <Text style={{ fontSize: 13, color: '#546e7a', display: 'block', marginBottom: 4 }}>Broadcast Name</Text>
                          <Input
                            placeholder="Broadcast name"
                            value={form.campaignName}
                            onChange={(e) => setFormField('campaignName', e.target.value)}
                          />
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <Text style={{ fontSize: 13, color: '#546e7a', display: 'block', marginBottom: 4 }}>Privacy</Text>
                          <Radio.Group
                            options={getPrivacyOptions()}
                            onChange={(e) => setFormField('campaignPrivacy', e.target.value === 'private')}
                            value={form.campaignPrivacy ? 'private' : 'public'}
                            optionType="button"
                            buttonStyle="solid"
                          />
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                            {form.campaignPrivacy
                              ? "Private: Only you can view this campaign"
                              : "Public: Visible to teammates with access"}
                          </Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <Text style={{ fontSize: 13, color: '#546e7a', display: 'block', marginBottom: 4 }}>Response Notifications</Text>
                          <Select
                            mode="multiple"
                            allowClear
                            placeholder="Select users to notify"
                            style={{ width: '100%' }}
                            value={form.notificationUsers}
                            onChange={(values) => setFormField('notificationUsers', values)}
                            tagRender={tagRender}
                            options={(users || []).map((u) => {
                              const name = u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim();
                              return {
                                label: (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0' }}>
                                    <UserAvatar name={name} src={u.avatar_url || u.avatar || u.photo} userId={u.id} size={36} showTooltip={false} />
                                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                      <span style={{ color: '#262626', fontSize: '14px', lineHeight: '1.2' }}>{name}</span>
                                      <span style={{ color: '#8c8c8c', fontSize: '13px', lineHeight: '1.2', marginTop: '4px' }}>{u.email}</span>
                                    </div>
                                  </div>
                                ),
                                value: String(u.id),
                                searchValue: `${name} ${u.email}`,
                              };
                            })}
                            filterOption={(input, option) =>
                              String(option?.searchValue ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                          />
                        </div>
                      </Space>
                 </div>

                 <div style={{ height: '1px', background: '#f0f0f0', margin: '16px 0' }} />

                 <div style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ margin: 0, marginBottom: 8 }}>When to send</Title>
                    {scheduleLapsed && (
                      <Text type="warning" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        The scheduled time for this draft has passed. It has been reset to "Send Now". Please re-schedule or proceed to send.
                      </Text>
                    )}
                    <Text style={{ fontSize: 13, color: '#546e7a', display: 'block', marginBottom: 4 }}>Schedule Type</Text>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Radio.Group
                        value={form.scheduleType}
                        onChange={(e) => setFormField('scheduleType', e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                        options={[
                          { label: <span style={{ fontWeight: 'normal' }}>Send Now</span>, value: 'now' },
                          { label: <span style={{ fontWeight: 'normal' }}>Schedule</span>, value: 'scheduled' }
                        ]}
                      />
                      {form.scheduleType === 'scheduled' && (
                        <Select
                          style={{ width: '100%' }}
                          value={form.timezone || undefined}
                          placeholder="Timezone"
                          onChange={(value) => setFormField('timezone', value)}
                          options={(timezones || []).map((tz) => ({
                            value: tz.value,
                            label: tz.name,
                          }))}
                        />
                      )}
                      {form.scheduleType === 'scheduled' && (
                        <DatePicker
                          showTime={{ use12Hours: true, format: "h:mm a" }}
                          format="YYYY-MM-DD h:mm a"
                          disabledDate={(current) => {
                            return current && current.isBefore(dayjs().startOf('day'));
                          }}
                          disabledTime={(current) => {
                            if (current && current.isSame(dayjs(), 'day')) {
                              const nowHour = dayjs().hour();
                              const nowMinute = dayjs().minute();
                              return {
                                disabledHours: () => [...Array(24).keys()].filter(h => h < nowHour),
                                disabledMinutes: (selectedHour) => {
                                  if (selectedHour === nowHour) {
                                    return [...Array(60).keys()].filter(m => m < nowMinute);
                                  }
                                  return [];
                                }
                              };
                            }
                            return {};
                          }}
                          style={{ width: '100%' }}
                          value={form.scheduleDate ? dayjs(form.scheduleDate) : null}
                          onChange={(dayjsValue) => setFormField('scheduleDate', dayjsValue ? dayjsValue.toISOString() : null)}
                          placeholder="Date Time"
                        />
                      )}
                    </Space>
                 </div>

                 <div style={{ height: '1px', background: '#f0f0f0', margin: '16px 0' }} />

                 <div style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Whom to send</Title>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      {form.audienceMode === 'group' && (
                        <Select
                          showSearch
                          placeholder="Select a group"
                          style={{ width: '100%' }}
                          value={form.groupId || undefined}
                          onChange={(value) => setFormField('groupId', value)}
                          optionFilterProp="label"
                          optionRender={(option) => (
                            <span><TeamOutlined style={{ marginRight: 8, color: '#8c8c8c' }} />{option.label}</span>
                          )}
                          options={(groups || [])
                            .slice()
                            .sort((a, b) => {
                              const aCount = Number(a.contact_count) || 0;
                              const bCount = Number(b.contact_count) || 0;
                              if (aCount === 0 && bCount > 0) return 1;
                              if (aCount > 0 && bCount === 0) return -1;
                              return (a.name || '').localeCompare(b.name || '');
                            })
                            .map((g) => ({
                              value: g.id,
                              label: `${g.name} (${g.contact_count || 0} contact${Number(g.contact_count) === 1 ? '' : 's'})`,
                              disabled: !g.contact_count || Number(g.contact_count) === 0,
                          }))}
                        />
                      )}
                      <Button type="link" size="small" style={{ padding: 0, marginTop: 4 }} icon={<PlusOutlined />} onClick={() => setShowImportModal(true)}>Create or import new group</Button>
                    </Space>
                 </div>
             </div>
          </Splitter.Panel>

          <Splitter.Panel min={300}>
             <div style={{ background: '#fff', borderLeft: '1px solid #f0f0f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '16px 24px', background: '#fff', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                   <Radio.Group
                      value={form.messageType}
                      onChange={(e) => setFormField('messageType', e.target.value)}
                      optionType="button"
                      buttonStyle="solid"
                      options={[
                        { label: <span style={{ fontWeight: 'normal' }}><MessageOutlined style={{ marginRight: 8, color: form.messageType === 'custom' ? '#fff' : '#8c8c8c' }} />Custom Message</span>, value: 'custom' },
                        { label: <span style={{ fontWeight: 'normal' }}><Space><RobotOutlined style={{ color: form.messageType === 'auto' ? '#fff' : '#8c8c8c' }} /> Automated Conversation</Space></span>, value: 'auto' }
                      ]}
                   />
                   <Space size="small">
                     {form.messageType === 'auto' && calendarUiEnabled && userHasCalendar && form.moduleGroupId && (
                       <Button
                         icon={<CalendarOutlined />}
                         onClick={() => {
                           setCalendarDrawerOpen(true);
                           if (!calendarPreview.schedules.length && !calendarPreview.loading) {
                             fetchCalendarPreview(getCalendarFromDate());
                           }
                         }}
                         style={{ fontSize: 13 }}
                       >
                         Calendar
                       </Button>
                     )}
                   </Space>
                </div>

                <div style={{ padding: '24px', flex: 1, overflowY: 'auto', background: '#fff' }}>

                  {form.messageType === 'auto' && (
                    <Space direction="vertical" style={{ width: '100%' }} size={16}>
                      <div>
                        <Title level={5} style={{ margin: 0, marginBottom: 4 }}>Agent</Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          Pick the automated conversation flow this broadcast will run.
                        </Text>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select an Agent"
                            style={{ flex: 1, minWidth: 0 }}
                            value={form.moduleGroupId || undefined}
                            onChange={(value) => setFormField('moduleGroupId', value || '')}
                            optionFilterProp="label"
                            optionRender={(option) => (
                              <Space>
                                <RobotOutlined />
                                {option.label}
                              </Space>
                            )}
                            options={(moduleGroups || []).map((mg) => {
                              // Calendar-booking agents need interview-scheduling.
                              const locked = mg.calendar_required && !canSchedule;
                              return {
                                value: mg.id,
                                label: locked ? `${mg.name} — not in your plan` : mg.name,
                                disabled: locked,
                              };
                            })}
                          />
                          <Button
                            icon={<BranchesOutlined />}
                            disabled={!form.moduleGroupId}
                            onClick={() => setFlowModalOpen(true)}
                          >
                            View flow
                          </Button>
                        </div>
                        {form.moduleGroupId && (() => {
                          const selected = (moduleGroups || []).find(mg => mg.id === form.moduleGroupId);
                          return selected?.description ? (
                            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
                              {selected.description}
                            </Text>
                          ) : null;
                        })()}
                      </div>
                     <ModuleGroupPreview
                        moduleGroupId={form.moduleGroupId}
                        moduleResponseOverrides={form.moduleResponseOverrides}
                        onOverridesChange={(v) => setFormField('moduleResponseOverrides', v)}
                        calendarRequired={calendarRequired}
                        userHasCalendar={userHasCalendar}
                        noSlotsMessage={form.noSlotsMessage}
                        onNoSlotsMessageChange={(v) => setFormField('noSlotsMessage', v)}
                        onCalendarConnect={() => history.push('/interview-invites?tab=integrations')}
                        extraValidMergeTokens={extraValidTokens}
                        flowPreviewMode="modal"
                        flowModalOpen={flowModalOpen}
                        onFlowModalClose={() => setFlowModalOpen(false)}
                      />
                      {calendarRequired && form.moduleGroupId && !canSchedule && (
                        <FeatureLockCard featureKey="interview-scheduling" height={180} />
                      )}
                      {calendarUiEnabled && form.moduleGroupId && (
                        <div>
                          <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Calendar Connection</Title>
                          <Text>This agent uses a calendar connection to schedule events.</Text>
                          <div style={{ marginBottom: 8 }}>
                            {userHasCalendar ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#52c41a' }}>
                                  <CheckCircleOutlined /> Calendar connected
                                </span>
                                {calendarPreview.loading ? (
                                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>Loading slots...</span>
                                ) : calendarPreview.availableSlotCount > 0 || calendarPreview.schedules.length > 0 ? (
                                  <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0, fontSize: 12 }}
                                    onClick={() => setCalendarDrawerOpen(true)}
                                  >
                                    {calendarPreview.availableSlotCount} slot{calendarPreview.availableSlotCount !== 1 ? 's' : ''} available
                                  </Button>
                                ) : null}
                              </div>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                <Text type="secondary" style={{ fontSize: 13 }}>Calendar not connected.</Text>
                                <Button
                                  type="link"
                                  size="small"
                                  style={{ padding: 0, fontSize: 13 }}
                                  onClick={() => history.push('/interview-invites?tab=integrations')}
                                >
                                  Connect Calendar
                                </Button>
                              </span>
                            )}
                          </div>
                          {hasSchedulingManager && (
                            <div style={{ margin: '10px 0 14px', padding: 12, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                                Whose calendar should interviews book on?
                              </div>
                              <Radio.Group
                                size="small"
                                value={form.schedulingMode}
                                onChange={(e) => setFormField('schedulingMode', e.target.value)}
                                style={{ marginBottom: 10 }}
                              >
                                <Radio.Button value="single">Single calendar</Radio.Button>
                                <Radio.Button value="round_robin">Round-robin (team)</Radio.Button>
                              </Radio.Group>

                              {form.schedulingMode === 'single' ? (
                                <CalendarOwnerPicker
                                  value={form.schedulingUserId}
                                  onChange={(v) => setFormField('schedulingUserId', v)}
                                  size="small"
                                  onSlotCounts={handleSlotCounts}
                                />
                              ) : (
                                <>
                                  <CalendarOwnerPicker
                                    multiple
                                    value={form.schedulingUserIds}
                                    onChange={(v) => setFormField('schedulingUserIds', v)}
                                    size="small"
                                    onSlotCounts={handleSlotCounts}
                                  />
                                  <div style={{ marginTop: 10 }}>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Distribution</div>
                                    <Radio.Group
                                      size="small"
                                      value={form.schedulingStrategy}
                                      onChange={(e) => setFormField('schedulingStrategy', e.target.value)}
                                    >
                                      <Radio value="least_loaded">Least-loaded</Radio>
                                      <Radio value="even_split">Even split</Radio>
                                    </Radio.Group>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          {userHasCalendar && (
                            <CampaignAvailabilitySettings
                              availabilitySettings={form.availabilitySettings}
                              onChange={(v) => setFormField('availabilitySettings', v)}
                            />
                          )}
                        </div>
                      )}
                    </Space>
                  )}

                  {form.messageType === 'custom' && (
                    <Space direction="vertical" style={{ width: '100%' }} size={16}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Text style={{ fontSize: 13, color: '#546e7a' }}>Send a broadcast message to your selected contact group at once — perfect for announcements, updates, or reminders.</Text>
                      </div>
                      <div>
                        <InterviewMessageInput
                          value={form.message}
                          onChange={(e) => setFormField('message', e.target.value)}
                          placeholder="Write your broadcast message"
                          rows={6}
                          errorMessage={formatInvalidTokenMessage(invalidTokens.message)}
                        />
                        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center' }}>
                          <Switch
                            size="small"
                            checked={form.shortenUrls}
                            onChange={(checked) => setFormField('shortenUrls', checked)}
                          />
                          <Text style={{ marginLeft: 8, fontSize: 13, color: '#546e7a' }}>Enable link shortening and click-tracking</Text>
                        </div>
                      </div>
                    </Space>
                  )}

                </div>
             </div>
          </Splitter.Panel>
        </Splitter>

        {/* Calendar Preview Drawer */}
        <Drawer
          title="Calendar Preview"
          placement="right"
          width={720}
          onClose={() => setCalendarDrawerOpen(false)}
          open={calendarDrawerOpen}
        >
          <CalendarPreview
            visible={calendarDrawerOpen}
            schedules={calendarPreview.schedules}
            loading={calendarPreview.loading}
            availableSlotCount={calendarPreview.availableSlotCount}
            timezone={
              form.timezone
                ? ((timezones || []).find((t) => t.value === form.timezone) || {}).zone || moment.tz.guess(true)
                : moment.tz.guess(true)
            }
            onDateChange={(newFromDate) => fetchCalendarPreview(newFromDate)}
            availabilitySettings={form.availabilitySettings}
          />
        </Drawer>
      </div>
      <ImportContactsModal
        visible={showImportModal}
        onCancel={() => setShowImportModal(false)}
        mode="execute"
        availableGroups={groups}
        requireGroup
        onFinish={async ({ groupId }) => {
          await refreshGroupsAndSelect(groupId);
        }}
      />

      {/* Preflight Review Modal */}
      <Modal
        title={(
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Test your campaign</div>
            <div style={{ fontSize: 12, fontWeight: 400, color: '#8c8c8c', marginTop: 2 }}>
              {noCampaignBot
                ? 'No broadcast campaign bot for this company — Send is disabled'
                : (calendarRequired && !userHasCalendar
                  ? 'Calendar required — connect your calendar to enable Send'
                  : (scheduleSummary && !scheduleSummary.valid
                    ? 'Schedule constraints incomplete — adjust availability to enable Send'
                    : (previewTerminal
                      ? 'Send is enabled'
                      : 'Reach the end of the flow to enable Send')))}
            </div>
          </div>
        )}
        open={showPreflightModal}
        onCancel={handleCancelPreflight}
        width={820}
        styles={{ body: { paddingTop: 8, paddingBottom: 8 } }}
        footer={[
          <Button key="reset" onClick={resetPreview} disabled={submitting}>Reset preview</Button>,
          <Button key="cancel" onClick={handleCancelPreflight}>Cancel</Button>,
          <Button
            key="create"
            type="primary"
            loading={submitting}
            onClick={handleCreate}
            disabled={
              !previewTerminal
              || noCampaignBot
              || (calendarRequired && !userHasCalendar)
              || (calendarRequired && scheduleSummary && !scheduleSummary.valid)
              || bookingHasNoSlots
            }
            style={(
              previewTerminal
              && !noCampaignBot
              && !(calendarRequired && !userHasCalendar)
              && !(calendarRequired && scheduleSummary && !scheduleSummary.valid)
              && !bookingHasNoSlots
            )
              ? { backgroundColor: '#38b549', borderColor: '#38b549' }
              : {}}
          >
            {noCampaignBot
              ? 'Campaign bot required to enable'
              : (calendarRequired && !userHasCalendar
                ? 'Connect calendar to enable'
                : (calendarRequired && scheduleSummary && !scheduleSummary.valid
                  ? 'Fix schedule constraints to enable'
                  : (bookingHasNoSlots
                    ? 'No available times to book'
                    : (previewTerminal ? 'Send Campaign' : 'Walk through the preview to enable'))))}
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', gap: 20, alignItems: 'stretch' }}>
        <div style={{
          width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column',
          gap: 10, justifyContent: 'center',
        }}>
          {/* Campaign Name */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>Campaign Name</Text>
              <Text strong style={{ fontSize: 13 }}>{form.campaignName || `${moment().format('MM/DD/YYYY HH:mm:ss')} Campaign`}</Text>
            </div>
          </div>

          {/* Privacy */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>Privacy</Text>
              <Text strong style={{ fontSize: 13 }}>{form.campaignPrivacy ? 'Private' : 'Public'}</Text>
            </div>
          </div>

          {/* Response Notifications */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>Response Notifications</Text>
              {getNotificationUserObjects().length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
                  {getNotificationUserObjects().map((u) => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <UserAvatar
                        name={u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim()}
                        src={u.avatar_url}
                        userId={u.id}
                        size={18}
                        showTooltip={false}
                      />
                      <Text strong style={{ fontSize: 12 }}>
                        {u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim()}
                      </Text>
                    </div>
                  ))}
                </div>
              ) : (
                <Text strong style={{ fontSize: 13 }}>None</Text>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>Schedule</Text>
              <Text strong style={{ fontSize: 13 }}>
                {form.scheduleType === 'now'
                  ? 'Send Now'
                  : `${moment(form.scheduleDate).format('MMM D, YYYY h:mm A')} (${getTimezoneName()})`}
              </Text>
            </div>
          </div>

          {/* Audience */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>Audience</Text>
              <Text strong style={{ fontSize: 13 }}>
                {`${getGroupName()} (${validationResult?.meta?.audienceCount ?? '?'} contacts)`}
              </Text>

              {/* Contact cycler — preview each recipient individually */}
              {previewAudience.contacts.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4,
                  padding: '2px 4px', background: '#f5f5f5', borderRadius: 12,
                  marginTop: 6,
                }}>
                  <Button
                    size="small"
                    type="text"
                    icon={<LeftOutlined />}
                    disabled={previewAudience.contacts.length <= 1}
                    onClick={() => cyclePreviewContact('prev')}
                  />
                  <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                    <Text strong style={{
                      fontSize: 12, display: 'block', lineHeight: '14px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {previewContact?.name || previewContact?.mobile || `Contact ${previewAudience.index + 1}`}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 10, lineHeight: '12px' }}>
                      Preview {previewAudience.index + 1} of {previewAudience.contacts.length}
                      {previewAudience.total > previewAudience.contacts.length ? `/${previewAudience.total}` : ''}
                    </Text>
                  </div>
                  <Button
                    size="small"
                    type="text"
                    icon={<span style={{ transform: 'rotate(180deg)', display: 'inline-block' }}><LeftOutlined /></span>}
                    disabled={previewAudience.contacts.length <= 1}
                    onClick={() => cyclePreviewContact('next')}
                  />
                </div>
              )}

              {form.messageType === 'auto' && calendarUiEnabled && userHasCalendar && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <Switch
                    size="small"
                    checked={!!previewSimulateNoSlots}
                    onChange={setPreviewSimulateNoSlots}
                  />
                  <Text style={{ fontSize: 12, color: '#546e7a' }}>Simulate full calendar</Text>
                </div>
              )}
            </div>
          </div>

          {/* Message / Flow */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>
                {form.messageType === 'custom' ? 'Message' : 'AI Agent'}
              </Text>
              {form.messageType === 'custom' ? (
                <Text strong style={{ wordBreak: 'break-word', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {form.message}
                </Text>
              ) : (
                <Text strong style={{ fontSize: 13 }}>{getModuleGroupName()}</Text>
              )}
            </div>
          </div>

          {/* Calendar (only when the selected agent requires one) */}
          {form.messageType === 'auto' && calendarRequired && !canSchedule && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <LockOutlined style={{ color: '#faad14', fontSize: 13, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>
                  Calendar
                </Text>
                <Text strong style={{ fontSize: 13 }}>Interview scheduling isn’t in your plan</Text>
              </div>
            </div>
          )}
          {form.messageType === 'auto' && calendarUiEnabled && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              {userHasCalendar ? (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
              ) : (
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 13, marginTop: 2 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>
                  Calendar {!userHasCalendar && <span style={{ color: '#ff4d4f' }}>(required)</span>}
                </Text>
                {userHasCalendar ? (
                  <div>
                    {previewOwnerId ? (
                      // Scheduling-manager: interviews book on the chosen teammate's
                      // calendar, so name them here instead of the sender's connection.
                      <Text strong style={{ fontSize: 13, display: 'block' }}>
                        {previewOwnerName}’s calendar · Connected
                      </Text>
                    ) : previewRoundRobin ? (
                      // Round-robin: no single calendar — each candidate is assigned one.
                      <div>
                        <Text strong style={{ fontSize: 13, display: 'block' }}>
                          Round-robin · {roundRobinCount} teammate{roundRobinCount === 1 ? '' : 's'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>
                          Availability varies by teammate
                        </Text>
                      </div>
                    ) : (
                      <>
                        <Text strong style={{ fontSize: 13, display: 'block', wordBreak: 'break-all' }}>
                          {userCalendarConnection?.email || 'Connected'}
                        </Text>
                        {userCalendarConnection?.calendarName && (
                          <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>
                            {userCalendarConnection.calendarName}
                          </Text>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <span>
                    <Text strong style={{ fontSize: 13, color: '#ff4d4f' }}>Not connected</Text>
                    {' — '}
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, fontSize: 12, height: 'auto' }}
                      onClick={() => history.push('/interview-invites?tab=integrations')}
                    >
                      Connect
                    </Button>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Booking calendar owner (scheduling-manager: teammate or round-robin) */}
          {bookingOwnerSummary && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>
                  {bookingOwnerSummary.title}
                </Text>
                <Text strong style={{ fontSize: 13, display: 'block' }}>
                  {bookingOwnerSummary.value}
                </Text>
              </div>
            </div>
          )}

          {/* Schedule constraints (only when calendar required AND connected) */}
          {scheduleSummary && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              {scheduleSummary.valid ? (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 13, marginTop: 2 }} />
              ) : (
                <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 13, marginTop: 2 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: '14px' }}>
                  Schedule constraints <span style={{ color: '#bfbfbf' }}>{scheduleSummary.sourceLabel}</span>
                </Text>
                {scheduleSummary.lines.map((line, i) => (
                  <Text
                    key={i}
                    strong={i === 0}
                    style={{ fontSize: 12, display: 'block', lineHeight: '16px' }}
                  >
                    {line}
                  </Text>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {validationResult?.warnings?.length > 0 && (
            <div style={{ marginTop: 4, padding: '10px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6 }}>
              {validationResult.warnings.map((w, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: i < validationResult.warnings.length - 1 ? 6 : 0 }}>
                  <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 14, marginTop: 2 }} />
                  <Text style={{ fontSize: 13 }}>{w.message}</Text>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column — mandatory live preview */}
        <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <LivePhonePreview
            messageType={form.messageType}
            modulegroupId={form.moduleGroupId}
            moduleResponseOverrides={form.moduleResponseOverrides}
            availabilitySettings={form.availabilitySettings}
            noSlotsMessage={form.noSlotsMessage}
            timezone={form.timezone}
            customMessage={form.message}
            notificationUsers={form.notificationUsers}
            contactId={previewContact?.id || null}
            onTerminalChange={setPreviewTerminal}
            resetKey={previewResetKey}
            simulateNoSlots={previewSimulateNoSlots}
            schedulingUserId={hasSchedulingManager && form.schedulingMode === 'single' ? form.schedulingUserId : undefined}
            schedulingUserIds={hasSchedulingManager && form.schedulingMode === 'round_robin' ? form.schedulingUserIds : undefined}
          />
        </div>
        </div>
      </Modal>
    </div>
  );
};

export default withRouter(CreateCampaignV3);
